import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, MessageSquare, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { deleteConversation, listConversations } from '@/lib/assistant';
import { cn } from '@/lib/utils';

export const conversationKeys = {
  all: ['assistant', 'conversations'] as const,
};

/** "3 min ago" / "Yesterday" — a wall-clock timestamp is not what you scan for. */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const mins = Math.round((Date.now() - then) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Past conversations, newest first.
 *
 * The list exists because the panel used to be the only copy of the transcript:
 * create someone, click through to their profile, and the record of what was
 * asked and what was done went with it.
 */
export function ConversationHistory({
  activeId,
  onOpen,
}: {
  activeId?: string;
  onOpen: (id: string) => void;
}) {
  const qc = useQueryClient();
  const { data: conversations = [], isLoading } = useQuery({
    queryKey: conversationKeys.all,
    queryFn: listConversations,
  });

  const remove = useMutation({
    mutationFn: deleteConversation,
    onSuccess: () => qc.invalidateQueries({ queryKey: conversationKeys.all }),
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Could not delete'),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <p className="px-1 py-8 text-center text-xs text-muted-foreground">
        No past conversations yet.
      </p>
    );
  }

  return (
    <ul className="space-y-1">
      {conversations.map((c) => (
        <li key={c.id} className="group relative">
          <button
            type="button"
            onClick={() => onOpen(c.id)}
            className={cn(
              'flex w-full cursor-pointer items-start gap-2 rounded-md border border-transparent px-2.5 py-2 pr-8 text-left hover:bg-muted',
              c.id === activeId && 'border-hygge/30 bg-hygge/5',
            )}
          >
            <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs font-medium text-foreground">{c.title}</span>
              <span className="mt-0.5 block text-[11px] text-muted-foreground">
                {relativeTime(c.updatedAt)} · {c.messageCount} messages
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => remove.mutate(c.id)}
            disabled={remove.isPending}
            aria-label={`Delete conversation ${c.title}`}
            className="absolute right-1.5 top-2 cursor-pointer rounded p-1 text-muted-foreground opacity-0 hover:bg-muted hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </li>
      ))}
    </ul>
  );
}
